/**
 * Valyou Care — Extended Protection Plan
 * Handles: PDP plan selection + chain add-to-cart, cart drawer one-tap add,
 * and cart reconciliation (warranty lines never outlive or outnumber the
 * items they cover).
 *
 * Integrates with the Impact theme's event bus:
 *   - listens:   variant:add (theme AJAX add), variant:change, cart:change
 *   - dispatches: cart:refresh  (Impact re-fetches cart + re-renders drawer)
 */
(function () {
  'use strict';
  if (window.__vpInit) return;
  window.__vpInit = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var warrantyCache = {}; // handle -> Promise<product.js JSON>

  /* ---------- pricing ---------- */

  // Mirrors the Liquid tier math in valyou-protection.liquid / -cart.liquid.
  // Tiers: $10–$500 in $10 steps, $520–$1,100 in $20 steps (values in cents).
  function tierFor(targetCents) {
    if (targetCents <= 51000) {
      var n = Math.round(targetCents / 1000);
      n = Math.min(50, Math.max(1, n));
      return n * 1000;
    }
    var k = Math.round((targetCents - 50000) / 2000);
    k = Math.min(30, Math.max(1, k));
    return 50000 + k * 2000;
  }

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  function fetchWarrantyProduct(handle) {
    if (!warrantyCache[handle]) {
      warrantyCache[handle] = fetch(root + 'products/' + handle + '.js').then(function (r) {
        if (!r.ok) throw new Error('warranty product missing: ' + handle);
        return r.json();
      });
    }
    return warrantyCache[handle];
  }

  // Exact price match preferred; falls back to nearest.
  function variantForTier(warrantyProduct, tierCents) {
    var best = null, bestDist = Infinity;
    for (var i = 0; i < warrantyProduct.variants.length; i++) {
      var v = warrantyProduct.variants[i];
      var d = Math.abs(v.price - tierCents);
      if (d < bestDist) { best = v; bestDist = d; }
      if (d === 0) break;
    }
    return best;
  }

  /* ---------- cart plumbing ---------- */

  function dispatchCartRefresh() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { baseEvent: 'cart:refresh', cart: null } })
    );
  }

  function addToCart(variantId, quantity, properties) {
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity, properties: properties }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart add failed');
      return r.json();
    });
  }

  function changeLine(key, quantity) {
    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  /* ---------- reconciler ---------- */
  // A warranty line must always map to a live covered line, and its quantity
  // may never exceed the covered quantity. Runs on load and on cart changes.

  var reconciling = false, reconcileQueued = false;

  function reconcile() {
    if (reconciling) { reconcileQueued = true; return; }
    reconciling = true;
    fetch(root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var fixes = [];
        cart.items.forEach(function (item) {
          var covers = item.properties && item.properties._vp_covers;
          if (!covers) return;
          var covered = null;
          cart.items.forEach(function (other) {
            if (other.variant_id === Number(covers) && !(other.properties && other.properties._vp_covers)) covered = other;
          });
          if (!covered) fixes.push({ key: item.key, qty: 0 });
          else if (item.quantity > covered.quantity) fixes.push({ key: item.key, qty: covered.quantity });
        });
        if (!fixes.length) return null;
        return fixes.reduce(function (p, f) {
          return p.then(function () { return changeLine(f.key, f.qty); });
        }, Promise.resolve()).then(function () { dispatchCartRefresh(); });
      })
      .catch(function () {})
      .then(function () {
        reconciling = false;
        if (reconcileQueued) { reconcileQueued = false; reconcile(); }
      });
  }

  var reconcileTimer = null;
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 400);
  }

  /* ---------- PDP widget ---------- */

  function initWidget(el) {
    if (el.__vpBound) return;
    el.__vpBound = true;

    var config;
    try {
      config = JSON.parse(el.querySelector('[data-vp-config]').textContent);
    } catch (e) { return; }

    var dialog = el.querySelector('[data-vp-dialog]');
    var confirmEl = el.querySelector('[data-vp-confirm]');
    var confirmText = el.querySelector('[data-vp-confirm-text]');
    var radios = el.querySelectorAll('input[name="vp-plan-' + config.productId + '"]');
    var currentVariantId = config.selectedVariantId || null;

    // Hide the widget if the warranty products aren't installed yet —
    // never show an offer that can't be fulfilled.
    Promise.all(config.handles.map(fetchWarrantyProduct)).catch(function () {
      el.style.display = 'none';
    });

    function selectedPlanIndex() {
      var checked = el.querySelector('input[name="vp-plan-' + config.productId + '"]:checked');
      return checked && checked.value !== 'none' ? Number(checked.value) : null;
    }

    function currentPrice() {
      if (currentVariantId && config.variantPrices[currentVariantId] != null) {
        return config.variantPrices[currentVariantId];
      }
      var first = Object.keys(config.variantPrices)[0];
      return config.variantPrices[first];
    }

    function updatePrices() {
      var price = currentPrice();
      var eligible = price >= config.minPrice && price <= config.maxPrice;
      el.style.display = eligible ? '' : 'none';
      if (!eligible) return;
      for (var i = 0; i < 3; i++) {
        var priceEl = el.querySelector('[data-vp-price="' + i + '"]');
        if (priceEl) priceEl.textContent = money(tierFor(price * config.rates[i]));
      }
      var idx = selectedPlanIndex();
      if (idx !== null) showArmedMessage(idx);
    }

    function showArmedMessage(idx) {
      confirmText.textContent = config.terms[idx] + '-Year Protection (' +
        money(tierFor(currentPrice() * config.rates[idx])) + ') will be added with your item.';
      confirmEl.hidden = false;
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        var idx = selectedPlanIndex();
        if (idx === null) { confirmEl.hidden = true; return; }
        showArmedMessage(idx);
        // Warm the cache so the chain add is instant.
        fetchWarrantyProduct(config.handles[idx]).catch(function () {});
      });
    });

    el.querySelectorAll('[data-vp-open-dialog]').forEach(function (btn) {
      btn.addEventListener('click', function () { if (dialog) dialog.showModal(); });
    });
    if (dialog) {
      var closeBtn = dialog.querySelector('[data-vp-close-dialog]');
      if (closeBtn) closeBtn.addEventListener('click', function () { dialog.close(); });
      dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); });
    }

    // Track variant changes from the theme's variant picker.
    document.addEventListener('variant:change', function (e) {
      var v = e.detail && e.detail.variant;
      if (v && config.variantPrices[v.id] != null) {
        currentVariantId = v.id;
        updatePrices();
      }
    });

    // Chain add: after the theme successfully adds this product, add the plan.
    document.addEventListener('variant:add', function (e) {
      var idx = selectedPlanIndex();
      if (idx === null || !e.detail || !e.detail.items) return;
      var added = null;
      e.detail.items.forEach(function (item) {
        var isWarranty = item.properties && item.properties._vp_covers;
        if (item.product_id === config.productId && !isWarranty) added = item;
      });
      if (!added) return;

      var basePrice = config.variantPrices[added.variant_id] != null
        ? config.variantPrices[added.variant_id]
        : (added.final_price != null ? added.final_price : added.price);
      var tier = tierFor(basePrice * config.rates[idx]);
      var planLabel = config.terms[idx] + '-Year Protection Plan';

      fetchWarrantyProduct(config.handles[idx])
        .then(function (wp) {
          var wv = variantForTier(wp, tier);
          if (!wv) throw new Error('no tier variant');
          return addToCart(wv.id, added.quantity, {
            _vp_covers: String(added.variant_id),
            'Plan': planLabel,
            'Protects': added.product_title || config.productTitle
          });
        })
        .then(function () {
          dispatchCartRefresh();
          confirmText.textContent = planLabel + ' added to your cart.';
          confirmEl.hidden = false;
          var decline = el.querySelector('input[name="vp-plan-' + config.productId + '"][value="none"]');
          if (decline) decline.checked = true;
        })
        .catch(function () {});
    });

    updatePrices();
  }

  function initAll() {
    document.querySelectorAll('[data-vp-widget]').forEach(initWidget);
  }

  /* ---------- cart drawer / cart page one-tap add ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-vp-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;
    btn.classList.add('vp-upsell__btn--busy');
    addToCart(Number(btn.getAttribute('data-vp-add')), Number(btn.getAttribute('data-vp-qty') || 1), {
      _vp_covers: btn.getAttribute('data-vp-covers'),
      'Plan': btn.getAttribute('data-vp-plan'),
      'Protects': btn.getAttribute('data-vp-protects')
    })
      .then(dispatchCartRefresh)
      .catch(function () { btn.disabled = false; btn.classList.remove('vp-upsell__btn--busy'); });
  });

  /* ---------- boot ---------- */

  document.addEventListener('cart:change', scheduleReconcile);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); scheduleReconcile(); });
  } else {
    initAll();
    scheduleReconcile();
  }
  // Widgets can arrive later (section rendering, quick view).
  new MutationObserver(function () { initAll(); }).observe(document.body || document.documentElement, { childList: true, subtree: true });
})();
